import type { WlTokens, WlTokensOverride } from './types'

/**
 * Default WL token set — reproduces the current `theme.css` HalalFresh look
 * for shops that HAVEN'T uploaded a pack yet. Everything here matches what
 * `--hf-*` variables in `theme.css` compute to today, so wiring the token
 * injector without shipping a pack produces a byte-identical WL render.
 *
 * When a shop uploads a pack, its `manifest.json.tokens` merges over these
 * via `mergeTokens()` — a pack that only defines `colors.primary` keeps
 * every other default from here.
 */
export const DEFAULT_WL_TOKENS: WlTokens = {
  colors: {
    // Matches theme.css current: `--hf-primary: var(--brand-theme-color, #0B5D34)`
    primary:      '#0B5D34',
    primaryInk:   '#ffffff',
    // Derived by injector (color-mix) if omitted, but explicit is clearer
    primaryDark:  '#082A18',
    primaryLight: '#F0F6F2',

    // Matches `--hf-accent: var(--brand-secondary-color, #D9B36C)`
    secondary:    '#D9B36C',
    secondaryInk: '#1F2A24',

    // Pop / accent — same as secondary by default; packs like NurPur split them
    accent:       '#D9B36C',
    accentInk:    '#1F2A24',

    // Page ground — the cream in current WL
    bg:           '#FAF8F3', // --hf-cream-50
    bgAlt:        '#F7F5F0', // --hf-cream-100

    // Deep ground for headers / heroes
    ink:          '#1F2A24', // --hf-black
    inkOn:        '#FAF8F3',

    // Body text
    bodyText:     '#2B2B2B', // --hf-charcoal
    mutedText:    '#6B746F', // --hf-muted

    border:       '#E6E1D8', // --hf-border

    // Status indicators — no defaults today; supply sensible ones for packs
    statusOpen:   '#22C55E',
    statusClosed: '#94A3B8',
    statusWarn:   '#F59E0B',
  },

  fonts: {
    display: {
      family:  'Playfair Display',
      source:  'google',
      weights: [600, 700],
    },
    body: {
      family:  'Poppins',
      source:  'google',
      weights: [400, 500, 600, 700, 800],
    },
    mono: {
      // No mono by default — the current WL doesn't use one. Packs like
      // NurPur override with DM Mono / Space Mono.
      family:  'ui-monospace',
      source:  'system',
    },
  },

  shape: {
    radiusSm:    '6px',   // --hf-radius-sm
    radiusMd:    '12px',  // --hf-radius-md
    radiusLg:    '20px',  // --hf-radius-lg
    radiusPill:  '999px', // --hf-radius-pill
    borderWidth: '1px',
    markRadius:  '8px',
  },

  motion: {
    fast:   '0.18s',
    medium: '0.32s',
    slow:   '0.9s',
    ease:   'cubic-bezier(0.4, 0, 0.2, 1)',
  },

  assets: {
    // All undefined by default — WL layout falls back to the shop's
    // `LogoUrl` (from the Shop entity) or the initials-avatar generator
    // when nothing is set. Same behaviour as today.
  },
}

/**
 * Merge a pack override onto the defaults — deep-merges by section but
 * shallow inside each section (fonts.display replaces defaults.fonts.display
 * as a whole, not per-key). Keeps the merge predictable: a pack that
 * overrides one font family gets that family with ITS declared weights, not
 * a Frankenstein of default weights + pack family.
 */
export function mergeTokens(base: WlTokens, override: WlTokensOverride | null | undefined): WlTokens {
  if (!override) return base
  return {
    colors: { ...base.colors, ...(override.colors ?? {}) },
    fonts: {
      display: override.fonts?.display ?? base.fonts.display,
      body:    override.fonts?.body    ?? base.fonts.body,
      mono:    override.fonts?.mono    ?? base.fonts.mono,
    },
    shape:  { ...base.shape,  ...(override.shape  ?? {}) },
    motion: { ...base.motion, ...(override.motion ?? {}) },
    assets: { ...base.assets, ...(override.assets ?? {}) },
  }
}
